import { App } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../hooks/use-app-selector.ts"
import { readProjectManagerRequired } from "../../../../Leads/helpers/project-manager-required.ts"
import { useProjectManagerPrompt } from "../../../../Leads/hooks/use-project-manager-prompt.tsx"
import { LEAD_STATUSES } from "../../../constants/lead-statuses.ts"
import {
  ArchiveLeadModal,
  type ArchiveLeadSubmission
} from "../../../../Leads/components/ArchiveLeadModal.tsx"
import { ARCHIVE_LEAD_STATUSES } from "../../../../Leads/constants/archive-lead-allowed-statuses.ts"
import {
  StatusHistoryPopover,
  type StatusUndoResult
} from "../../../../StatusAutomation/components/StatusHistoryPopover.tsx"
import { StatusSuggestionChips } from "../../../../StatusAutomation/components/StatusSuggestionChips.tsx"
import {
  readStatusRefusal,
  refusalMessage,
  statusDisplay
} from "../../../../StatusAutomation/helpers/status-automation.ts"
import type {
  StatusSuggestion,
  StatusSuggestionAcceptBody,
  StatusTransition
} from "../../../../StatusAutomation/types/status-automation.types.ts"
import {
  acceptLeadStatusSuggestion,
  dismissLeadStatusSuggestion,
  undoLeadStatusTransition
} from "../../../redux/lead-status-automation.thunks.ts"
import { fetchLead } from "../../../redux/lead.thunks.ts"
import type { Lead } from "../../../types/lead.types.ts"

const closesTheLead = (target: number | null) =>
  target !== null &&
  (ARCHIVE_LEAD_STATUSES as readonly number[]).includes(target)

interface LeadStatusSuggestionsProps {
  lead: Lead
  /** `lead_edit`: the right every accept, dismiss and undo on a lead needs. */
  canEdit: boolean
  /**
   * The stage the missing-fields banner is holding. Its Move retries the same
   * move once the fields are filled, so a chip for that stage would be a
   * second control for one move.
   */
  pendingTarget?: number | null
}

export function LeadStatusSuggestions({
  lead,
  canEdit,
  pendingTarget = null
}: LeadStatusSuggestionsProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const dispatch = useAppDispatch()
  const [busyId, setBusyId] = useState<string | null>(null)
  // Lost and Nurturing archive the lead, so they ask what the Archive asks.
  const [closing, setClosing] = useState<StatusSuggestion | null>(null)

  const currentUserId = useAppSelector((state) => state.auth?.userInfo?.id)

  // A Won that converts the lead into a project asks for its PM first, through
  // the same prompt the status change raises, and is resent with the choice.
  const projectManagerPrompt = useProjectManagerPrompt<StatusSuggestion>({
    submit: (suggestion, projectManagerId) =>
      send(suggestion, {
        prompt_project_manager: true,
        project_manager_id: projectManagerId
      }).catch((rejection: unknown) => {
        warnIfRefused(rejection)
        throw rejection
      }),
    defaultProjectManagerId: currentUserId
  })

  const send = async (
    suggestion: StatusSuggestion,
    body?: StatusSuggestionAcceptBody
  ) => {
    await dispatch(
      acceptLeadStatusSuggestion({
        leadId: lead.id,
        suggestionId: suggestion.id,
        target: suggestion.target,
        ...(body ? { body } : {})
      })
    ).unwrap()
    message.success(
      suggestion.target === null
        ? t("statusAutomation.toasts.acknowledged")
        : t("statusAutomation.toasts.moved", {
            status: statusDisplay("lead", suggestion.target, t)
          })
    )
    // The header's stage, archive banner and next suggestions all follow.
    void dispatch(fetchLead(lead.id))
  }

  const warnIfRefused = (rejection: unknown) => {
    const refusal = readStatusRefusal(rejection)
    if (refusal) message.warning(refusalMessage(refusal, t))
    return !!refusal
  }

  const accept = async (suggestion: StatusSuggestion) => {
    if (closesTheLead(suggestion.target)) {
      setClosing(suggestion)
      return
    }
    setBusyId(suggestion.id)
    try {
      await send(
        suggestion,
        suggestion.target === LEAD_STATUSES.WON
          ? { prompt_project_manager: true }
          : undefined
      )
    } catch (rejection) {
      const required = readProjectManagerRequired(rejection)
      if (required) {
        projectManagerPrompt.ask(required, suggestion)
        return
      }
      // Anything but a 409 was already answered by the thunk.
      warnIfRefused(rejection)
    } finally {
      setBusyId(null)
    }
  }

  const dismiss = async (suggestion: StatusSuggestion) => {
    setBusyId(suggestion.id)
    try {
      await dispatch(
        dismissLeadStatusSuggestion({
          leadId: lead.id,
          suggestionId: suggestion.id
        })
      ).unwrap()
      message.success(t("statusAutomation.toasts.dismissed"))
    } catch (rejection) {
      warnIfRefused(rejection)
    } finally {
      setBusyId(null)
    }
  }

  // The dialog's diary question rides a rejection; a 409 closes it instead.
  const submitClose = async (submission: ArchiveLeadSubmission) => {
    if (!closing) return
    // The suggestion already names the target; only the dialog's answers go.
    const answers: Partial<ArchiveLeadSubmission> = { ...submission }
    delete answers.target_status
    try {
      await send(closing, answers)
    } catch (rejection) {
      if (!warnIfRefused(rejection)) throw rejection
    }
  }

  return (
    <>
      {projectManagerPrompt.dialog}
      <StatusSuggestionChips
        kind={"lead"}
        recordId={lead.id}
        suggestions={
          pendingTarget == null
            ? lead.status_suggestions
            : lead.status_suggestions?.filter(
                (suggestion) => suggestion.target !== pendingTarget
              )
        }
        canAct={() => canEdit}
        busyId={busyId}
        onAccept={(suggestion) => void accept(suggestion)}
        onDismiss={(suggestion) => void dismiss(suggestion)}
      />
      {closing && (
        <ArchiveLeadModal
          open
          leadId={lead.id}
          leadName={lead.lead_name}
          statuses={[closing.target as number]}
          submit={submitClose}
          onCancel={() => setClosing(null)}
          onArchived={() => setClosing(null)}
        />
      )}
    </>
  )
}

interface LeadStatusHistoryProps {
  leadId: string
  canEdit: boolean
}

export function LeadStatusHistory({ leadId, canEdit }: LeadStatusHistoryProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const dispatch = useAppDispatch()

  const undo = async (
    transition: StatusTransition
  ): Promise<StatusUndoResult> => {
    try {
      const answer = await dispatch(
        undoLeadStatusTransition({ leadId, transitionId: transition.id })
      ).unwrap()
      message.success(t("statusAutomation.toasts.undone"))
      void dispatch(fetchLead(leadId))
      return { ok: true, transitions: answer?.status_transitions }
    } catch (rejection) {
      return { ok: false, refusal: readStatusRefusal(rejection) }
    }
  }

  return (
    <StatusHistoryPopover
      kind={"lead"}
      recordId={leadId}
      canUndo={canEdit}
      onUndo={undo}
    />
  )
}
